import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  TestsFull_tests_pagination,
  AppUsageAnalytics_appUsageAnalytics_surveys,
  getFormattedDateAndTime,
  AppUsageAnalytics_appUsageAnalytics_surveys_User_Groups,
  AppUsageAnalytics_appUsageAnalytics_surveys_User_Companies,
  getSurveyStatusLabelColor,
  QuestionnaireQuestionKeys,
  getQuestionnaireResponsesSummary,
  AppUsageAnalytics_appUsageAnalytics_surveys_SurveyResponses,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Label from 'components/Label'
import Card from 'components/Card'
import { Box } from 'components/Layout'

interface ColumnLabels {
  answerDate?: string | null
  clearance?: string | null
  patientName?: string | null
  patientAddress?: string | null
  patientGender?: string | null
  patientRace?: string | null
  patientEthnicity?: string | null
  patientGroups?: string | null
  patientCompanies?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  surveys: AppUsageAnalytics_appUsageAnalytics_surveys[]
  pagination?: TestsFull_tests_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
}

const defaultColumnLabels = {
  answerDate: 'Answer date',
  clearance: 'Clearance',
  patientName: 'Patient',
  patientAddress: 'Address',
  patientGender: 'Gender',
  patientRace: 'Race',
  patientEthnicity: 'Ethnicity',
  patientGroups: 'Groups',
  patientCompanies: 'Companies',
}

const PAGE_LENGTH = 10

export const AnalyticsAppUsageDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  surveys,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  const getGroupsCompanies = (
    groups: AppUsageAnalytics_appUsageAnalytics_surveys_User_Groups[],
    companies: AppUsageAnalytics_appUsageAnalytics_surveys_User_Companies[]
  ) => {
    const userGroupsComponent = (
      <Box>
        {groups.map((group: AppUsageAnalytics_appUsageAnalytics_surveys_User_Groups) => (
          <Box>{`${group.id} - ${group.name}`}</Box>
        ))}
      </Box>
    )
    const userCompaniesComponent = (
      <Box>
        {companies.map((company: AppUsageAnalytics_appUsageAnalytics_surveys_User_Companies) => (
          <Box>{`${company.id} - ${company.name}`}</Box>
        ))}
      </Box>
    )

    return { userGroupsComponent, userCompaniesComponent }
  }

  const getClearance = (
    SurveyResponses: AppUsageAnalytics_appUsageAnalytics_surveys_SurveyResponses[]
  ) => {
    const isUserCleared = SurveyResponses.find(
      ({ key }) => QuestionnaireQuestionKeys.TEMPERATURE_IN_FAHRENHEIT === key
    )?.value
    if (isUserCleared) {
      const responsesSummary = getQuestionnaireResponsesSummary(SurveyResponses)
      const {
        isTemperatureInTolerance,
        hasOnlyNegativeAnswers,
        hasPositiveTestWithinLastTenDays,
        hasTraveled,
      } = responsesSummary
      const reasonsNotCleared = []
      if (!isTemperatureInTolerance) reasonsNotCleared.push('Temperature not in tolerance')
      if (!hasOnlyNegativeAnswers) reasonsNotCleared.push('Symtoms')
      if (hasPositiveTestWithinLastTenDays) reasonsNotCleared.push('Positive results last ten days')

      if (isTemperatureInTolerance && hasOnlyNegativeAnswers && !hasPositiveTestWithinLastTenDays) {
        if (hasTraveled) {
          return (
            <>
              <Label indicator="yellow">Flagged</Label>
              <Box>Has traveled</Box>
            </>
          )
        }
        return <Label indicator="green">Cleared</Label>
      }
      return (
        <>
          <Label indicator="red">Not cleared</Label>
          <Box>
            {reasonsNotCleared.map((reason) => (
              <Box>{reason}</Box>
            ))}
          </Box>
        </>
      )
    }
    return (
      <>
        <Label indicator="blue">No temperature found</Label>
      </>
    )
  }

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={surveys}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.answerDate) ? (
                <Datalist.HeaderCell label={columnLabels.answerDate} />
              ) : null}
              {!isNull(columnLabels.clearance) ? (
                <Datalist.HeaderCell label={columnLabels.clearance} />
              ) : null}
              {!isNull(columnLabels.patientName) ? (
                <Datalist.HeaderCell label={columnLabels.patientName} />
              ) : null}
              {!isNull(columnLabels.patientAddress) ? (
                <Datalist.HeaderCell label={columnLabels.patientAddress} />
              ) : null}
              {!isNull(columnLabels.patientGender) ? (
                <Datalist.HeaderCell label={columnLabels.patientGender} />
              ) : null}
              {!isNull(columnLabels.patientRace) ? (
                <Datalist.HeaderCell label={columnLabels.patientRace} />
              ) : null}
              {!isNull(columnLabels.patientEthnicity) ? (
                <Datalist.HeaderCell label={columnLabels.patientEthnicity} />
              ) : null}
              {!isNull(columnLabels.patientGroups) ? (
                <Datalist.HeaderCell label={columnLabels.patientGroups} />
              ) : null}
              {!isNull(columnLabels.patientCompanies) ? (
                <Datalist.HeaderCell label={columnLabels.patientCompanies} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const { createdAt, status, SurveyResponses, statusReason, User } = data
            const clearance = getClearance(SurveyResponses)
            const { Address, Groups, Companies } = User
            const { userGroupsComponent, userCompaniesComponent } = getGroupsCompanies(
              Groups,
              Companies
            )
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.answerDate) ? (
                  <Datalist.Cell label={columnLabels.answerDate}>
                    {getFormattedDateAndTime(createdAt, true)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.clearance) ? (
                  <Datalist.Cell label={columnLabels.clearance}>
                    {status ? (
                      <>
                        <Label indicator={status ? getSurveyStatusLabelColor(status) : 'blue'}>
                          {status}
                        </Label>
                        {statusReason && statusReason.map((reason) => <Box>{reason}</Box>)}
                      </>
                    ) : (
                      clearance
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patientName) ? (
                  <Datalist.Cell>
                    {User.firstName} {User.lastName} ({getFormattedDateAndTime(User.birthDate)})
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patientAddress) ? (
                  <Datalist.Cell>
                    {Address?.street}, {Address?.city}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patientGender) ? (
                  <Datalist.Cell label={columnLabels.patientGender}>{User.gender}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patientRace) ? (
                  <Datalist.Cell label={columnLabels.patientRace}>{User.race}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patientEthnicity) ? (
                  <Datalist.Cell label={columnLabels.patientEthnicity}>
                    {User.ethnicity}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patientGroups) ? (
                  <Datalist.Cell label={columnLabels.patientGroups}>
                    {userGroupsComponent}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patientCompanies) ? (
                  <Datalist.Cell label={columnLabels.patientCompanies}>
                    {userCompaniesComponent}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
